import { CDK_DRAG_PARENT, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';

import { BoardsAbstract } from '@main-application/boards/abstract/board-abstract.component';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import {
  AreaPageElementRatingGroupModel,
  AreaPageModel,
  TemplateEditorRatingGroup,
} from '@template/models/template-editor-model';
import { TemplateEditorService } from '@template/services/template-editor.service';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@UntilDestroy()
@Component({
  selector: 'app-template-editor-item',
  templateUrl: './template-editor-item.component.html',
  styleUrls: ['./template-editor-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateEditorItemComponent extends BoardsAbstract implements OnInit, AfterViewInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  editMode = false;
  ratingGroups$ = this.templateEditorService.getRatingGroups();
  openRatingPopup = false;

  @Output() remove = new EventEmitter<AreaPageModel>();
  @Output() itemChange = new EventEmitter<AreaPageModel>();
  @Output() enterPressed = new EventEmitter<AreaPageModel>();
  @Output() ratingGroupAdd = new EventEmitter<[AreaPageModel, TemplateEditorRatingGroup]>();
  @Output() ratingGroupRemove = new EventEmitter<AreaPageModel>();

  @Input() item: AreaPageModel;
  @ViewChild('itemName', { read: ElementRef, static: false }) itemName: ElementRef<HTMLDivElement>;
  @ViewChild(CdkDragHandle, { static: true }) handle: CdkDragHandle;

  constructor(
    protected store: Store<{}>,
    protected cdr: ChangeDetectorRef,
    snackbarService: SnackbarService,
    @Optional() @Inject(CDK_DRAG_PARENT) public cdk,
    private templateEditorService: TemplateEditorService
  ) {
    super(store, snackbarService);
  }

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.cdk._handles.length = 1;
    this.cdk._handles._results = [this.handle];
    this.cdk._handles.changes.next();
  }

  get name() {
    return this.item.name;
  }

  get ratingGroup() {
    const item = this.item.elements?.find(
      element => element.type === 'propertystate'
    ) as AreaPageElementRatingGroupModel;
    return item;
  }

  ratingGroupChange(ratingGroup: AreaPageElementRatingGroupModel) {
    this.itemChange.emit({
      ...this.item,
      elements: this.item.elements.map(element => {
        if (element.type === 'propertystate') {
          return ratingGroup;
        } else {
          return element;
        }
      }),
    });
  }
  enterEditMode() {
    if (!this.checkEditRight() || this.editMode) {
      return;
    }
    this.editMode = true;
    this.cdr.detectChanges();
    const div = this.itemName.nativeElement;
    setTimeout(() => {
      const range = document.createRange();
      range.selectNodeContents(div);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }, 0);
  }

  nameChanged($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      this.itemName.nativeElement.blur();
      this.enterPressed.emit(this.item);
      $event.stopPropagation();
      $event.preventDefault();
    }
  }

  leaveEditMode(value: string) {
    value = value.trim();
    this.editMode = false;
    if (this.item.title !== value) {
      this.itemChange.emit({ ...this.item, title: value });
    }

    setTimeout(() => {
      const div = this.itemName.nativeElement;
      div.focus();
      div.blur();
    }, 1);
  }

  removeClick() {
    this.remove.emit(this.item);
  }

  removeRatingGroup(event: MouseEvent) {
    if (this.ratingGroup) {
      event.preventDefault();
      event.stopPropagation();
      this.ratingGroupRemove.emit(this.item);
    }
  }

  addRatingGroup(ratingGroup: TemplateEditorRatingGroup) {
    this.ratingGroupAdd.emit([this.item, ratingGroup]);
  }

  toggleRating() {
    this.openRatingPopup = !this.openRatingPopup;
  }
}
